import React from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiPaperclip } from "react-icons/fi";

import { formatFileSize } from "@core/reports/constants/mediaAttachments";

export type UploadDraftRow = {
  file: File;
  caption: string;
};

type Props = {
  show: boolean;
  categoryLabel: string;
  rows: UploadDraftRow[];
  busy: boolean;
  error: string | null;
  onCaptionChange: (index: number, caption: string) => void;
  onSave: () => void;
  onCancel: () => void;
};

/**
 * Confirms files before they are attached to the department report.
 * Save persists via report/save; Cancel discards the selection.
 */
export default function DepartmentReportUploadModal({
  show,
  categoryLabel,
  rows,
  busy,
  error,
  onCaptionChange,
  onSave,
  onCancel,
}: Props) {
  return (
    <Modal
      show={show}
      onHide={() => {
        if (!busy) onCancel();
      }}
      centered
      backdrop={busy ? "static" : true}
      keyboard={!busy}
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Attach file{rows.length === 1 ? "" : "s"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="text-muted small">Category</div>
          <div className="fw-medium">{categoryLabel}</div>
        </div>

        {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

        <div className="d-flex flex-column gap-3">
          {rows.map((row, index) => {
            const captionId = `upload-caption-${index}`;
            return (
              <div key={`${row.file.name}-${row.file.size}-${index}`} className="border rounded px-3 py-3">
                <div className="d-flex align-items-start gap-2 mb-2 min-width-0">
                  <FiPaperclip className="text-muted flex-shrink-0 mt-1" aria-hidden />
                  <div className="min-width-0">
                    <div className="fw-medium text-truncate">{row.file.name}</div>
                    <div className="text-muted small">{formatFileSize(row.file.size)}</div>
                  </div>
                </div>
                <Form.Group controlId={captionId}>
                  <Form.Label className="small mb-1">Caption</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    maxLength={500}
                    value={row.caption}
                    placeholder="Add a short description (optional)"
                    onChange={(event) => onCaptionChange(index, event.target.value)}
                    disabled={busy}
                  />
                </Form.Group>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={onSave} disabled={busy || rows.length === 0}>
          {busy ? <Spinner animation="border" size="sm" className="me-2" aria-hidden /> : null}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
